'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Pregnancy by Trimester: A Week-by-Week Guide"
      author="MicroPlastics Editorial Team"
      date="May 17, 2026"
      readTime="14 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics during pregnancy by trimester, week-by-week guide"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Microplastics have been detected in the placenta (Ragusa 2021), amniotic
        fluid, and breast milk. <strong>First trimester (weeks 1–13)</strong>
        is when reducing exposure matters most, because organs and
        the nervous system form. <strong>Second trimester (14–27)</strong>
        priorities shift to thyroid- and brain-supporting nutrient density.
        <strong> Third trimester (28–40)</strong> focuses on placental
        microplastic load and preparing the nursery. The three changes that
        matter most across all trimesters: <strong>filter drinking water,
        eliminate plastic food storage and heating, and switch to natural-fibre
        bedding.</strong>
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastics cross the placental barrier, confirmed in 2021 by Ragusa et al. and replicated in every subsequent placenta study.</>,
          <>First trimester (1–13 weeks): organ and neural-tube formation; chemical disruption has the largest developmental effect.</>,
          <>Second trimester (14–27): thyroid and brain development continue; reducing endocrine-disrupting chemical exposure matters most here.</>,
          <>Third trimester (28–40): rapid growth + placental accumulation; nursery prep window.</>,
          <>Across all trimesters, the three changes that matter most are filtered water, no plastic food storage or heating, and natural-fibre bedding.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the placenta studies show
      </h2>
      <p>
        Antonio Ragusa and colleagues at the San Giovanni Calibita Hospital in
        Rome published the first detection of microplastics in human placenta in
        <em> Environment International</em> in 2021. They examined six placentas
        from healthy pregnancies and found microplastic particles in four of
        them, including pigmented fragments consistent with synthetic dyes used
        in cosmetics and packaging.
      </p>
      <p>
        Subsequent studies have replicated the finding and detected microplastics
        in <strong>amniotic fluid</strong> (Halfar et al. 2023), <strong>breast
        milk</strong> (Ragusa et al. 2022), and <strong>cord blood</strong>
        (multiple 2024 studies). The maternal–fetal barrier does not effectively
        filter microplastic particles.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        First trimester (weeks 1–13): when it matters most
      </h2>
      <p>
        This is when the neural tube closes, the heart begins to beat, and
        organ systems form. Endocrine disruption during these weeks has the
        largest developmental impact per microgram of exposure. Priorities:
      </p>

      <BlogComparisonTable
        caption="First trimester action priorities (weeks 1–13)"
        columns={[
          { key: 'priority', label: 'Priority' },
          { key: 'why', label: 'Why now' },
          { key: 'action', label: 'Action' },
        ]}
        rows={[
          { priority: '1. Switch all drinking water to filtered tap', why: 'Bottled water averages 240,000 nanoparticles/L (PNAS 2024)', action: 'RO or NSF 401 carbon-block filter' },
          { priority: '2. Eliminate plastic food storage', why: '4M particles per microwave cycle (Hussain 2023)', action: 'Glass containers throughout' },
          { priority: '3. Stop using canned food', why: 'Can liners are major BPA/BPS source', action: 'Fresh or frozen alternatives' },
          { priority: '4. Replace non-stick cookware', why: 'PFAS exposure during cooking', action: 'Cast iron or stainless steel' },
          { priority: '5. Review prenatal vitamins', why: 'Some capsules use plastic polymers (HPMC, PEG)', action: 'See linked guide below' },
          { priority: '6. Skip thermal receipts', why: 'BPA absorbs through skin', action: 'Decline paper receipts; ask for email' },
          { priority: '7. Avoid hot drinks in paper cups', why: '25,000 particles per 15 min (Ranjan 2022)', action: 'Bring ceramic mug or stainless travel cup' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Second trimester (weeks 14–27): supporting brain & thyroid development
      </h2>
      <p>
        Fetal thyroid is forming and maternal thyroid hormones remain critical
        through ~20 weeks. Brain development continues at rapid pace. Iodine,
        omega-3s, and choline become especially important.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Maintain all first-trimester changes</strong> as foundations.</li>
        <li><strong>Choose glass-bottled or carton-only beverages</strong> over plastic.</li>
        <li><strong>Use a HEPA air purifier in the bedroom</strong>, indoor air contains 3-15× more microplastics than outdoor.</li>
        <li><strong>Switch to natural-fibre clothing</strong>, phthalates and brominated flame retardants in synthetics affect maternal thyroid.</li>
        <li><strong>Choose plastic-free personal care</strong>, check moisturisers, sunscreens, lip products for PEG, polyethylene, acrylates copolymer, carbomer. See our{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            polymer-free toothpaste guide
          </Link>.
        </li>
        <li><strong>Choose glass-jarred yogurt</strong> if you eat dairy regularly. Greek yogurt&apos;s acidity accelerates leaching from PS/PP cups.</li>
        <li><strong>Maintain adequate iodine intake</strong> (150-220 µg/day from prenatal + diet), counters environmental thyroid disruption.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Third trimester (weeks 28–40): growth, placenta, nursery
      </h2>
      <p>
        Fetal growth is rapid (the baby roughly triples in weight). Placental
        microplastic load tends to accumulate over the pregnancy. This is
        nursery-preparation time and breast-feeding-prep time.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Set up a microplastic-aware nursery.</strong> Wood crib, organic cotton bedding, no synthetic carpets, HEPA filter. See our{' '}
          <Link href="/blog/microplastics-nursery-guide" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            microplastic-free nursery guide
          </Link>.
        </li>
        <li><strong>Choose baby bottles ahead of time.</strong> Glass or silicone, never PP for primary feeding. See{' '}
          <Link href="/blog/best-baby-bottles-microplastic-free" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best baby bottles without microplastics
          </Link>.
        </li>
        <li><strong>If formula-feeding, source it now.</strong> European glass-bottled brands need lead time to ship.</li>
        <li><strong>Stock plastic-free food storage</strong> for the postpartum period, you&apos;ll be reheating leftovers and storing milk.</li>
        <li><strong>Buy natural-fibre swaddles and sleep sacks</strong>, organic cotton, bamboo, or merino wool.</li>
        <li><strong>Consider an air purifier for the nursery</strong> in addition to the bedroom.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 5-minute trimester checklist
      </h2>
      <BlogComparisonTable
        caption="Trimester-by-trimester quick checklist"
        columns={[
          { key: 'item', label: 'Item' },
          { key: 't1', label: 'T1 (1–13)' },
          { key: 't2', label: 'T2 (14–27)' },
          { key: 't3', label: 'T3 (28–40)' },
        ]}
        rows={[
          { item: 'Filter drinking water (RO or NSF 401)', t1: '✓', t2: '✓', t3: '✓' },
          { item: 'Eliminate plastic food storage', t1: '✓', t2: '✓', t3: '✓' },
          { item: 'Avoid canned food', t1: '✓', t2: '✓', t3: '✓' },
          { item: 'Replace non-stick cookware', t1: '✓', t2: '✓', t3: '✓' },
          { item: 'Glass-jarred yogurt/dairy', t1: '—', t2: '✓', t3: '✓' },
          { item: 'Natural-fibre clothing & bedding', t1: '—', t2: '✓', t3: '✓' },
          { item: 'HEPA bedroom air filter', t1: '—', t2: '✓', t3: '✓' },
          { item: 'Polymer-free personal care', t1: '—', t2: '✓', t3: '✓' },
          { item: 'Source baby bottles', t1: '—', t2: '—', t3: '✓' },
          { item: 'Source baby formula (if needed)', t1: '—', t2: '—', t3: '✓' },
          { item: 'Microplastic-aware nursery', t1: '—', t2: '—', t3: '✓' },
        ]}
      />

      <p>
        See related pregnancy/baby guides:{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in pregnancy
        </Link>,{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          BPA-free pregnancy guide
        </Link>,{' '}
        <Link href="/blog/microplastics-in-breast-milk" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in breast milk
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-prenatal-vitamins" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in prenatal vitamins
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics cross the placenta?',
            answer:
              'Yes. Ragusa et al. confirmed microplastics in 4 of 6 human placentas in 2021. Subsequent studies have detected microplastics in amniotic fluid, cord blood, and breast milk. The maternal-fetal barrier does not effectively filter microplastic particles.',
          },
          {
            question: 'Which trimester is most important for reducing microplastic exposure?',
            answer:
              'First trimester (weeks 1-13). This is when organs and the neural tube form, so endocrine-disrupting chemicals have the largest developmental impact per microgram of exposure. The most important changes are filtered drinking water, eliminating plastic food storage, and avoiding canned food.',
          },
          {
            question: 'What is the safest pregnancy diet for microplastic reduction?',
            answer:
              'Fresh or frozen food rather than canned, glass-jarred dairy and condiments, organic when possible (reduces agricultural microplastic), filtered water, and skipping plastic-wrapped takeout and ultra-processed food. Maintain adequate iodine and omega-3 intake.',
          },
          {
            question: 'Do I need to worry about microplastics in the third trimester?',
            answer:
              'Yes. Fetal growth is rapid and placental microplastic load accumulates over pregnancy. Use the third trimester to set up the nursery, source baby bottles and formula, stock plastic-free food storage for postpartum, and choose natural-fibre swaddles.',
          },
          {
            question: 'Can I detox microplastics during pregnancy?',
            answer:
              'No "detox" protocol has been validated. The realistic approach is to reduce ongoing intake, which immediately lowers placental and breast milk transfer. Adequate hydration with filtered water, high-fibre diet, and supporting normal liver/kidney function are reasonable supportive measures.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Ragusa A, Svelato A, Santacroce C, et al.',
            year: 2021,
            title: 'Plasticenta: First evidence of microplastics in human placenta',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412020322297',
          },
          {
            authors: 'Ragusa A, Notarstefano V, Svelato A, et al.',
            year: 2022,
            title: 'Raman microspectroscopy detection and characterisation of microplastics in human breastmilk',
            publisher: 'Polymers',
            url: 'https://www.mdpi.com/2073-4360/14/13/2700',
          },
          {
            authors: 'Halfar J, Čabanová K, Vávra K, et al.',
            year: 2023,
            title: 'Microplastics and additives in patients with preterm birth: The first evidence in human amniotic fluid',
            publisher: 'Chemosphere',
            url: 'https://www.sciencedirect.com/science/article/pii/S0045653523011190',
          },
          {
            authors: 'World Health Organization',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'WHO',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'Gore AC, Chappell VA, Fenton SE, et al.',
            year: 2015,
            title: 'EDC-2: The Endocrine Society Scientific Statement on Endocrine-Disrupting Chemicals',
            publisher: 'Endocrine Reviews',
            url: 'https://academic.oup.com/edrv/article/36/6/E1/2354691',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
